import {
  MenuItemOptionFragment,
  MenuOptionItemFragment,
  MenuOptionMenuItemFragment,
} from '@/generated/graphql'
import { getCustomerFacingPriceItem } from '@/helpers/customerPricing/customerPricing'
import {
  Replace,
  RequireFields,
  isNotNil,
  areFieldsNotNil,
} from '@caterdesk/utils--gm-validation/types'

import { startOfDay } from 'date-fns'

export type MenuOptionItem = Replace<
  MenuOptionItemFragment,
  { item: RequireFields<MenuOptionMenuItemFragment, 'name'> }
> & { vendorPrice: number | null; vendorPriceExTax: number | null }
export type MenuItemOption = Replace<MenuItemOptionFragment, { items: MenuOptionItem[] }>

const isWithinActiveWindow = (
  item: Pick<MenuOptionMenuItemFragment, 'activeFrom' | 'activeTo'>,
  referenceDate: Date,
): boolean => {
  const reference = startOfDay(referenceDate)
  if (item.activeFrom) {
    const activeFrom = startOfDay(new Date(item.activeFrom))
    if (!Number.isNaN(activeFrom.getTime()) && activeFrom > reference) return false
  }
  if (item.activeTo) {
    const activeTo = startOfDay(new Date(item.activeTo))
    if (!Number.isNaN(activeTo.getTime()) && activeTo < reference) return false
  }
  return true
}

export const createMenuItemOption = (
  option: MenuItemOptionFragment,
  referenceDate: Date = new Date(),
): MenuItemOption => ({
  ...option,
  items: (option.items || [])
    .filter(isNotNil)
    .filter(areFieldsNotNil(['item']))
    .filter((i) => i.item.isActive)
    .filter((i) => isWithinActiveWindow(i.item, referenceDate))
    .flatMap((optionItem) => {
      const customerPriceItem = getCustomerFacingPriceItem(optionItem.customerPriceItems)
      return optionItem.item.name
        ? [
            {
              ...optionItem,
              price: customerPriceItem.price,
              priceExTax: customerPriceItem.priceExTax,
              // Raw fragment prices = what the vendor charges; preserved for
              // vendor-audience surfaces before the customer overwrite above.
              vendorPrice: optionItem.price ?? null,
              vendorPriceExTax: optionItem.priceExTax ?? null,
              item: {
                ...optionItem.item,
                name: optionItem.item.name,
              },
            },
          ]
        : []
    }),
})
